/**
 * Flight Budget Reconciliation
 *
 * The invariant "flight budgets sum to the line item's budgetTotal" holds at
 * creation time (blueprint apply, Copy from Campaign, CSV import) but nothing
 * maintained it afterwards: every budget editor rewrote budgetTotal without
 * touching flights, silently corrupting delivery data.
 *
 * rescaleFlightsToBudget is the reconcile step those editors now call: it
 * redistributes the new budget across the existing flights, preserving each
 * flight's current budget proportion (its phasing) and dealing rounding in
 * integer cents so the flights sum exactly to the new budget. Flights with
 * no budget split evenly.
 */

import { distributeCentsProRata } from './renewalFlights';

/**
 * Rescale flight budgets so they sum exactly to `newBudget`.
 *
 * @param {Array} flights – existing flight schedule entries
 * @param {number} newBudget – the line item's new budgetTotal
 * @returns {Array} new flights array with rescaled budgets, or the original
 *          array when there is nothing to do
 */
export const rescaleFlightsToBudget = (flights, newBudget) => {
  if (!Array.isArray(flights) || flights.length === 0) return flights;

  const target = Number(newBudget);
  if (!Number.isFinite(target) || target < 0) return flights;

  const currentTotal = flights.reduce((sum, f) => sum + (Number(f.budget) || 0), 0);
  if (Math.abs(currentTotal - target) < 0.005) return flights;

  const weights = flights.map((f) => Number(f.budget) || 0);
  const shares = distributeCentsProRata(Math.round(target * 100), weights);

  return flights.map((flight, i) => ({
    ...flight,
    budget: shares[i] / 100,
  }));
};

/**
 * Rescale whichever flight field exists on a line item (the codebase uses
 * both `flightSchedule` and `flightSchedules`), returning a new line item.
 * No-op when the line item has no flights.
 *
 * @param {Object} lineItem
 * @param {number} newBudget
 * @returns {Object} new line item object (same reference when unchanged)
 */
export const rescaleLineItemFlightsToBudget = (lineItem, newBudget) => {
  if (!lineItem) return lineItem;
  const field = Array.isArray(lineItem.flightSchedule)
    ? 'flightSchedule'
    : Array.isArray(lineItem.flightSchedules)
      ? 'flightSchedules'
      : null;
  if (!field) return lineItem;

  const rescaled = rescaleFlightsToBudget(lineItem[field], newBudget);
  if (rescaled === lineItem[field]) return lineItem;
  return { ...lineItem, [field]: rescaled };
};
